import { IndustryWorkspaceTemplates } from '../setup/IndustryWorkspaceTemplates'
import {ActivationGuidance} from '../setup/ActivationGuidance'
import {ActivationPolicyEditor} from '../setup/ActivationPolicyEditor'
import { useAuth } from '../../context/AuthContext'
import { AdminListStateProvider } from '../../context/AdminListStateContext'
import { WorkerOnboardingPolicyPanel } from './admin/WorkerOnboardingPolicyPanel'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAccessContext } from '../../context/AccessContext'
import { useI18n } from '../../i18n/I18nContext'
import { AdminOverviewPanel } from './admin/AdminOverviewPanel'
import { MessagesPanel } from './admin/MessagesPanel'
import { BrandingPanel } from './admin/BrandingPanel'
import { JobsPanel } from './admin/JobsPanel'
import { LocationsPanel } from './admin/LocationsPanel'
import { AdminPageHeader } from './admin/AdminUiPrimitives'
import { OrganizationsPanel } from './admin/OrganizationsPanel'
import { OrgRelationshipsPanel } from './admin/OrgRelationshipsPanel'
import { OrgTreePanel } from './admin/OrgTreePanel'
import { SchedulePanel } from './admin/SchedulePanel'
import { SkillsGovernancePanel } from './admin/SkillsGovernancePanel'
import { ReportsPanel } from './admin/ReportsPanel'
import { SupervisorDashboardPanel } from './admin/SupervisorDashboardPanel'
import { WorkersPanel } from './admin/WorkersPanel'
import { EmploymentCompliancePanel } from './admin/EmploymentCompliancePanel'
import { TimesheetPayrollExportPanel } from './admin/TimesheetPayrollExportPanel'
import { UsersAccessPanel } from './admin/UsersAccessPanel'
import { WhatsappSettingsPanel } from './admin/WhatsappSettingsPanel'
import { AutoPunchSettingsPanel } from './admin/AutoPunchSettingsPanel'
import { AdminExperienceSettingsPanel } from './admin/AdminExperienceControls'
import { AdminProcessGuide } from './admin/AdminProcessGuide'
import { useAdminExperience } from '../../context/AdminExperienceContext'
import { AdminHelp, type AdminHelpTopic } from './admin/AdminHelp'
import {
  brandColors,
  brandingLogo,
  brandingName,
  brandingOrgId,
  formatOrgType,
} from './admin/adminPanelUtils'
import { getAccessibleLocationOwnerOrgId, type DelegatedJobContext, type LocationJobDraft } from './admin/adminNavigation'
import type { AccessRole, AccessSetupState, AccessTenant, AccessibleLocation, GrantedClientLocation, OrganizationBranding, RelationshipAccessEntry } from '../../types/access'
import { formatTenantLabel } from '../../utils/displayLabels'
import { roleDisplayLabel } from '../../utils/enumDisplayLabels'
import { createSystemTenant, getOnboardingReadiness, updateSystemTenantIndustry } from '../../services/access'
import { availableIndustryTypes, getIndustryPreset, type IndustryType } from '../../domain/industryPresets'
import { PUNCH_NAVIGATE_EVENT, navigateTo } from '../../services/navigation'
import { BillingFeatureGate } from '../billing/BillingFeatureGate'
import { ProjectWorkLogAdminPanel } from './worklog/WorkLogWorkspace'
import { DecisionAppealsPanel } from './admin/DecisionAppealsPanel'
import { PredictiveForecastingPanel } from './admin/PredictiveForecastingPanel'
import { LabourStandardsPanel, LocationPrivacyAdminPanel, OpenApiStandardsPanel, WebhookStandardsPanel } from './admin/WorkforceStandardsPanel'
import { openAdminRealtimeStream, schedulePanelInvalidation, type AdminPanelKey } from '../../services/adminRealtime'
import { useIndustryTerminology, type IndustryLabelKey } from '../../hooks/useIndustryTerminology'
import { adminNavigationForExperience, adminNavigationLabel } from './admin/adminNavigationPresentation'
import {
  adminTabFromLocation,
  adminTabUrl,
  groupedAdminNavigationEntries,
  hasExplicitTenantContext,
  type AdminNavigationEntry,
  type AdminTab,
  type NavigationAccessInput,
} from './admin/navigationRegistry'

const focusedWorkerProfileIdFromLocation = (): string => {
  if (typeof window === 'undefined') return ''
  return new URLSearchParams(window.location.search).get('workerProfileId') || ''
}

const adminHelpTopics: Record<AdminTab, AdminHelpTopic> = {
  employment: {
    title: 'Employment & Compliance help',
    purpose: 'Configure organization requirements and review purpose-specific worker submissions.',
    permissions: 'Employment management and verification are separate from protected payroll permissions.',
    nextActions: ['Configure requirements.', 'Request information from an active worker.', 'Review submitted evidence before deciding.'],
    safety: 'Production SIN/banking collection remains disabled pending independent security acceptance.'
  },
  overview: {
    title: 'Network Dashboard help',
    purpose: 'Use the Network Dashboard to see workforce demand, supply, allocation, compliance, execution, and settlement signals in the current scope.',
    permissions: 'Your visible metrics and shortcuts reflect your current tenant, organization, OU, and location scope.',
    nextActions: ['Review demand and supply health.', 'Switch organization context when you manage more than one org.', 'Open the module that owns the next operational step.'],
    safety: 'If an action is missing, you may need additional permissions or a narrower organization/location context.'
  },
  organizations: {
    title: 'Organizations help',
    purpose: 'Organizations model agencies and companies inside the tenant.',
    permissions: 'Create and edit controls appear only when your role can manage organizations.',
    nextActions: ['Create agency or company records.', 'Add branding from Settings.', 'Use relationships to connect companies and agencies.'],
    safety: 'Organization visibility is scoped; hidden orgs are not available through this workspace.'
  },
  relationships: {
    title: 'Network Relationships help',
    purpose: 'Network Relationships describe how agencies, companies, subcontractors, and partner organizations share workforce capacity and work demand.',
    permissions: 'Manage options require organization relationship or organization management permission.',
    nextActions: ['Review active connections.', 'Add or end relationships when business ownership changes.', 'Confirm the selected organization before saving.'],
    safety: 'Relationship changes can affect worker sharing, job visibility, and assignment workflows.'
  },
  'org-tree': {
    title: 'Org Structure help',
    purpose: 'Org Structure shows operational units, sites, projects, locations, and inherited access boundaries inside the selected organization.',
    permissions: 'Users with view access can inspect the tree; manage access is needed to change structure.',
    nextActions: ['Select an organization.', 'Review location ownership.', 'Use OU context to understand inherited roles.'],
    safety: 'Scoped users see only the branches and locations available to their access context.'
  },
  'users-access': {
    title: 'Roles & Access help',
    purpose: 'Users & Access is for inviting admins, supervisors, and staff, then assigning scoped roles.',
    permissions: 'Invite, suspend, and role controls are shown only when your permissions allow them.',
    nextActions: ['Invite users by normalized email or phone.', 'Assign roles at the smallest useful scope.', 'Audit role assignments before changing tenant-wide access.'],
    safety: 'Do not grant broad tenant roles when an organization, OU, or location role is enough.'
  },
  workers: {
    title: 'Workforce help',
    purpose: 'Workforce is the directory for global worker profiles, worker organization relationships, work areas, owned profile data, and documents.',
    permissions: 'Profile edits, sharing, and document controls require worker management or sharing permissions.',
    nextActions: ['Search by name, email, or phone.', 'Open worker details intentionally.', 'Attach workers to organizations through active relationships.'],
    safety: 'Contact and document visibility follows tenant/org scope; hidden actions require additional permissions.'
  },
  skills: {
    title: 'Skills help',
    purpose: 'Skills governs requested skills, canonical catalog quality, aliases, and duplicate merges.',
    permissions: 'Request moderation and merge actions require skill catalog management access.',
    nextActions: ['Review pending requests.', 'Map typos and variants to canonical skills.', 'Merge duplicates after checking impact.'],
    safety: 'Merges deprecate source skills and move canonical references without hard-deleting history.'
  },
  schedule: {
    title: 'Scheduling help',
    purpose: 'Scheduling shows shift boards, applications, assignments, and shift-level allocation work.',
    permissions: 'Create, assign, approve, duplicate, or cancel controls appear only with job/assignment permissions.',
    nextActions: ['Filter by date/location/job.', 'Open a shift for matched workers.', 'Jump to Jobs or Workers from shift details.'],
    safety: 'Schedule rows are limited to visible jobs, locations, and organizations.'
  },
  jobs: {
    title: 'Demand / Jobs help',
    purpose: 'Demand / Jobs is for creating workforce demand, shifts, pay/bill context, and worker assignments.',
    permissions: 'Posting, editing, assignment, and approval controls depend on job and assignment permissions.',
    nextActions: ['Create or update a job.', 'Add shifts with location and rate context.', 'Review matches, applications, and assignments.'],
    safety: 'Bill-rate details are kept off worker-facing surfaces and scoped by admin permissions.'
  },
  worklog: {
    title: 'Project Work Log help',
    purpose: 'Project Work Log manages project tasks and weekly task-time submissions separately from punches and workforce shifts.',
    permissions: 'Project, task, approval, and export controls follow dedicated tenant permissions and billing entitlements.',
    nextActions: ['Create or select a project.', 'Assign tasks with exact user or worker identities.', 'Review submitted weekly project timesheets.'],
    safety: 'Project time never changes punch-derived shift evidence or payroll timesheets.'
  },
  locations: {
    title: 'Locations & Geofences help',
    purpose: 'Locations and geofences define where work happens and how punch validation is enforced.',
    permissions: 'Location and geofence edits require location management permission.',
    nextActions: ['Confirm owner organization.', 'Check geofence status.', 'Review live worker state when permitted.'],
    safety: 'Workers can only punch into assignment locations that pass tenant, assignment, and geofence checks.'
  },
  timesheets: {
    title: 'Approvals / Timesheets help',
    purpose: 'Approvals / Timesheets summarize shift evidence, punch events, breaks, offline review status, worker disputes, comments, and approval readiness.',
    permissions: 'Review actions require timesheet or punch review permissions in the current scope.',
    nextActions: ['Filter by date, worker, location, job, agency, or company.', 'Review pending offline punches and disputed rows.', 'Approve, reject, or comment before settlement.'],
    safety: 'Open shifts, disputed rows, and pending offline punches should be resolved before payroll export.'
  },
  'decision-reviews': {
    title: 'Decision Reviews help',
    purpose: 'Decision Reviews provides a real human-review queue for material worker access decisions and submitted evidence.',
    permissions: 'Queue and actions follow tenant, organization, and location-scoped decision-review permissions.',
    nextActions: ['Open a submitted appeal.', 'Review the original explanation and worker statement.', 'Request information, reject, or approve with a resulting action.'],
    safety: 'Reviewers never see unrelated workers, hidden rates, internal fraud thresholds, or out-of-scope tenant data.'
  },
  payroll: {
    title: 'Payroll Export help',
    purpose: 'Payroll Export prepares CSV output from approved timesheet and assignment data.',
    permissions: 'Export buttons appear only with payroll/export or timekeeping review access.',
    nextActions: ['Set the payroll date range.', 'Narrow by location or organization when needed.', 'Review CSV preview fields before download.'],
    safety: 'Exports follow visible scope and should be treated as sensitive payroll-adjacent data.'
  },
  messages: {
    title: 'Messages help',
    purpose: 'Messages lets authorized admins communicate with locations, active workers, or individual workers.',
    permissions: 'Read and send controls follow message permissions and location/worker scope.',
    nextActions: ['Select an accessible location.', 'Choose the audience.', 'Refresh threads after sending.'],
    safety: 'Workers and locations outside your scope are not rendered as send targets.'
  },
  reports: {
    title: 'Reports help',
    purpose: 'Reports summarizes live scoped workforce, schedule fill, attendance exceptions, and approval readiness.',
    permissions: 'Report availability depends on timesheet, export, punch review, or broad admin permissions.',
    nextActions: ['Set the date and operational filters.', 'Review exceptions and approval rows.', 'Open Payroll Export when approved rows are ready.'],
    safety: 'Reports follow backend tenant, organization, and location scope and do not expose pay or bill rates.'
  },
  forecasting: {
    title: 'Predictive Forecasting help',
    purpose: 'Forecast total workforce demand by canonical site and apply stable permanent FTE as a separate planning calculation.',
    permissions: 'View, manage, and training actions require separate backend forecasting permissions and tenant entitlement.',
    nextActions: ['Discover tenant sites.', 'Train and compare baselines with ML challengers.', 'Review demand, intervals, drivers, and temporary staffing need.'],
    safety: 'Forecasts are planning aids. Baselines remain champion unless an ML challenger clears the configured improvement threshold.'
  },
  settings: {
    title: 'Settings help',
    purpose: 'Settings covers organization branding and notification-channel readiness.',
    permissions: 'Branding updates require branding or organization management permission. WhatsApp setup requires tenant settings, message management, or admin access.',
    nextActions: ['Pick the organization context.', 'Update display name, logo, colors, and support contacts.', 'Confirm WhatsApp provider readiness before enabling delivery.'],
    safety: 'Settings changes are tenant or organization scoped and do not grant permissions.'
  }
}

const setupChecklistItems = (
  setupState: AccessSetupState | undefined,
  canTroubleshootPunches: boolean,
  labels: (key: IndustryLabelKey) => string,
  explicitIndustry: boolean,
) => [
  { label: 'Organization created', complete: Boolean(setupState?.hasOrganization) },
  { label: explicitIndustry ? `${labels('location')} added` : 'Location added', complete: Boolean(setupState?.hasLocation) },
  { label: explicitIndustry ? `${labels('worker')} invited/added` : 'Worker invited/added', complete: Boolean(setupState?.hasWorkers) },
  { label: explicitIndustry ? `${labels('job')} created` : 'Job/order created', complete: Boolean(setupState?.hasJobs) },
  { label: explicitIndustry ? `${labels('worker')} assigned` : 'Worker assigned', complete: Boolean(setupState?.hasAssignments) },
  {
    label: 'Test punch completed or punch troubleshooting available',
    complete: Boolean(setupState?.hasTestPunchCompleted || canTroubleshootPunches),
  },
]

const SetupChecklist: React.FC<{
  setupState?: AccessSetupState
  canTroubleshootPunches: boolean
  labels: (key: IndustryLabelKey) => string
  explicitIndustry: boolean
}> = ({ setupState, canTroubleshootPunches, labels, explicitIndustry }) => {
  const { t } = useI18n()
  const items = setupChecklistItems(setupState, canTroubleshootPunches, labels, explicitIndustry)
  const completeCount = items.filter((item) => item.complete).length
  if (setupState && completeCount === items.length) return null

  return (
    <article className="panel workspace-panel admin-setup-card stack" data-testid="admin-setup-checklist">
      <div className="admin-panel-toolbar">
        <div className="stack" style={{ gap: '.25rem' }}>
          <h3 style={{ margin: 0 }}>{t('Setup checklist')}</h3>
          <p className="text-muted">
            {setupState
              ? `${completeCount} / ${items.length} ${t('setup steps are complete.')}`
              : t('Setup status is not available from the access context yet.')}
          </p>
        </div>
      </div>
      <div className="admin-checklist-grid">
        {items.map((item) => (
          <div key={item.label} className={`admin-checklist-item ${item.complete ? 'complete' : ''}`}>
            <span aria-hidden="true">{item.complete ? t('Done') : t('Todo')}</span>
            <strong>{t(item.label)}</strong>
          </div>
        ))}
      </div>
      {!setupState ? (
        <p className="helper">{t('Refresh the workspace. If this keeps happening, ask an owner to verify the access context response.')}</p>
      ) : null}
    </article>
  )
}

const SetupReminderCard: React.FC<{ readiness: any | null }> = ({ readiness }) => {
  if (!readiness || readiness.completed) return null
  const missingRequired = (readiness.checks || []).filter((check: any) => check.required && check.status !== 'complete')
  const next = readiness.nextCheck || missingRequired[0]

  return (
    <article className="panel workspace-panel admin-setup-card stack" data-testid="admin-setup-reminder">
      <div className="admin-panel-toolbar">
        <div className="stack" style={{ gap: '.25rem' }}>
          <h3 style={{ margin: 0 }}>Tenant setup progress</h3>
          <p className="text-muted">{readiness.percentComplete || 0}% complete. {next ? `Next: ${next.label}.` : 'Review remaining setup items.'}</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => {
          navigateTo('/setup')
        }}>
          Resume setup
        </button>
      </div>
      <div className="setup-meter" aria-hidden="true"><span style={{ width: `${readiness.percentComplete || 0}%` }} /></div>
      {missingRequired.length ? (
        <div className="admin-checklist-grid">
          {missingRequired.slice(0, 4).map((check: any) => (
            <div key={check.key} className="admin-checklist-item">
              <span>Todo</span>
              <strong>{check.label}</strong>
            </div>
          ))}
        </div>
      ) : null}
    </article>
  )
}

const hasCustomizedBranding = (branding?: OrganizationBranding, organizationName?: string) => {
  if (!branding) return false
  return Boolean(
    brandingLogo(branding)
    || brandColors(branding).length > 0
    || (brandingName(branding, organizationName).trim() && brandingName(branding, organizationName).trim() !== (organizationName || '').trim())
    || branding.supportEmail
    || branding.support_email
    || branding.supportPhone
    || branding.support_phone
    || branding.websiteUrl
    || branding.website_url
  )
}

const readableScopeType = (scopeType?: string) => {
  if (scopeType === 'organization_unit') return 'OU'
  return String(scopeType || 'tenant').replace(/_/g, ' ')
}

// Resolve a role to a localizable display label: prefer the canonical role_key
// mapped through the centralized enum formatter (which localizes via t()), and
// fall back to the backend-provided label or id. The canonical role_key/id is
// never mutated — only the display string changes.
const accessRoleLabel = (role: AccessRole, t: (value: string) => string) => {
  if (role.role_key) return t(roleDisplayLabel(role.role_key))
  return t(role.label || role.id || '')
}

const currentRoleSummary = (roles: AccessRole[] = [], t: (value: string) => string) => {
  const labels = Array.from(new Set(roles.map((role) => accessRoleLabel(role, t)).filter(Boolean)))
  if (labels.length === 0) return t('No role grants returned')
  const visible = labels.slice(0, 3).join(', ')
  return labels.length > 3 ? t('{visible} +{count} more').replace('{visible}', visible).replace('{count}', String(labels.length - 3)) : visible
}

const looksLikeUuid = (value?: string | null) =>
  Boolean(value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value))

const safeTenantLabel = (tenant: AccessTenant | null | undefined, tenantId?: string | null) => {
  const label = formatTenantLabel(tenant || undefined, tenantId || undefined)
  return looksLikeUuid(label) ? 'Selected tenant' : label
}

const industryAdminHelpTopic = (
  tab: AdminTab,
  topic: AdminHelpTopic,
  labels: ReturnType<typeof useIndustryTerminology>,
): AdminHelpTopic => {
  if (!labels.explicitIndustry) return topic
  const worker = labels.label('worker').toLowerCase()
  const workers = labels.label('workers').toLowerCase()
  const job = labels.label('job').toLowerCase()
  const jobs = labels.label('jobs')
  const location = labels.label('location').toLowerCase()
  const locations = labels.label('locations').toLowerCase()
  const shift = labels.label('shift').toLowerCase()
  const shifts = labels.label('shifts').toLowerCase()
  const assignment = labels.label('assignment').toLowerCase()
  const assignments = labels.label('assignments').toLowerCase()

  if (tab === 'workers') {
    return {
      ...topic,
      title: `${labels.label('workers')} help`,
      purpose: `${labels.label('workers')} is the directory for profiles, organization relationships, work areas, owned profile data, and documents.`,
      permissions: `Profile edits, sharing, and document controls require ${worker} management or sharing permissions.`,
      nextActions: ['Search by name, email, or phone.', `Open ${worker} details intentionally.`, `Attach ${workers} to organizations through active relationships.`],
    }
  }
  if (tab === 'schedule') {
    return {
      ...topic,
      title: `${labels.label('shifts')} schedule help`,
      purpose: `Scheduling shows ${shift} boards, applications, ${assignments}, and ${shift}-level allocation work.`,
      nextActions: [`Filter by date/${location}/${job}.`, `Open a ${shift} for matched ${workers}.`, `Jump to ${jobs} or ${labels.label('workers')} from ${shift} details.`],
      safety: `Schedule rows are limited to visible ${jobs.toLowerCase()}, ${locations}, and organizations.`,
    }
  }
  if (tab === 'jobs') {
    return {
      ...topic,
      title: `${jobs} help`,
      purpose: `${jobs} is for creating demand, ${shifts}, pay/bill context, and ${worker} ${assignments}.`,
      nextActions: [`Create or update a ${job}.`, `Add ${shifts} with ${location} and rate context.`, `Review matches, applications, and ${assignments}.`],
    }
  }
  if (tab === 'locations') {
    return {
      ...topic,
      title: `${labels.label('locations')} & Geofences help`,
      purpose: `${labels.label('locations')} and geofences define where work happens and how punch validation is enforced.`,
      permissions: `${labels.label('location')} and geofence edits require location management permission.`,
      nextActions: ['Confirm owner organization.', 'Check geofence status.', `Review live ${worker} state when permitted.`],
      safety: `${labels.label('workers')} can only punch into ${assignment} ${locations} that pass tenant, ${assignment}, and geofence checks.`,
    }
  }
  if (tab === 'timesheets') {
    return {
      ...topic,
      title: `${labels.label('timesheet')} Approvals help`,
      nextActions: [`Filter by date, ${worker}, ${location}, ${job}, agency, or company.`, 'Review pending offline punches and disputed rows.', 'Approve, reject, or comment before settlement.'],
    }
  }
  if (tab === 'payroll') {
    return {
      ...topic,
      title: `${labels.label('payroll')} Export help`,
      purpose: `${labels.label('payroll')} Export prepares CSV output from approved ${labels.label('timesheet').toLowerCase()} and ${assignment} data.`,
      nextActions: [`Set the ${labels.label('payroll').toLowerCase()} date range.`, `Narrow by ${location} or organization when needed.`, 'Review CSV preview fields before download.'],
    }
  }
  if (tab === 'messages') {
    return {
      ...topic,
      purpose: `Messages lets authorized admins communicate with ${locations}, active ${workers}, or individual ${workers}.`,
      permissions: `Read and send controls follow message permissions and ${location}/${worker} scope.`,
      nextActions: [`Select an accessible ${location}.`, 'Choose the audience.', 'Refresh threads after sending.'],
      safety: `${labels.label('workers')} and ${locations} outside your scope are not rendered as send targets.`,
    }
  }
  if (tab === 'reports') {
    return {
      ...topic,
      purpose: `Reports summarizes live scoped ${workers}, schedule fill, attendance exceptions, and approval readiness.`,
      safety: `Reports follow backend tenant, organization, and ${location} scope and do not expose pay or bill rates.`,
    }
  }
  return topic
}

type AdminWorkspaceProps = {
  initialJobDraft?: LocationJobDraft | null
  onConsumeInitialJobDraft?: () => void
}

export const AdminWorkspace: React.FC<AdminWorkspaceProps> = props => {
  const { user, loading: authLoading } = useAuth()
  const { accessContext: context, currentOrganization, selectedTenantId, loading, error } = useAccessContext()
  const ready = !authLoading && !loading && !error && Boolean(user?.id && context?.tenantId
    && context.currentUser?.id === user.id && (!selectedTenantId || selectedTenantId === context.tenantId))
  const scopeKey = ready ? JSON.stringify([
    user!.id, context!.tenantId, currentOrganization?.id || '',
    context!.effectivePermissionKeys, context!.effectivePermissions,
    context!.billingEntitlements, context!.accessibleLocations?.map(row => row.id),
  ]) : null
  return <AdminListStateProvider scopeKey={scopeKey}><AdminWorkspaceContent {...props} /></AdminListStateProvider>
}

const AdminWorkspaceContent: React.FC<AdminWorkspaceProps> = ({ initialJobDraft, onConsumeInitialJobDraft }) => {
  const { t } = useI18n()
  const { isSimple } = useAdminExperience()
  const {
    accessContext,
    currentOrganization,
    loading,
    error,
    refresh,
    setCurrentOrganizationId,
    setCurrentTenantId,
    selectedTenantId,
    hasAnyPermission,
  } = useAccessContext()
  const industryLabels = useIndustryTerminology()
  const [activeTab, setActiveTab] = useState<AdminTab>(() => adminTabFromLocation() || 'overview')
  const [preOpenRelationshipId, setPreOpenRelationshipId] = useState('')
  const [pendingDelegatedJobContext, setPendingDelegatedJobContext] = useState<DelegatedJobContext | null>(null)
  const [highlightClientOrgId, setHighlightClientOrgId] = useState('')
  const [focusedJobId, setFocusedJobId] = useState('')
  const [pendingJobDraft, setPendingJobDraft] = useState<LocationJobDraft | null>(null)
  const [focusedWorkerProfileId, setFocusedWorkerProfileId] = useState(focusedWorkerProfileIdFromLocation)
  const [selectedOuId, setSelectedOuId] = useState('')
  const [selectedLocationId, setSelectedLocationId] = useState('')
  const [setupReadiness, setSetupReadiness] = useState<any | null>(null)
  const [newTenantName, setNewTenantName] = useState('')
  const [newTenantSlug, setNewTenantSlug] = useState('')
  const [creatingTenant, setCreatingTenant] = useState(false)
  const [createTenantError, setCreateTenantError] = useState('')
  const [industrySaving, setIndustrySaving] = useState(false)
  const [industryStatus, setIndustryStatus] = useState('')
  const [showAdminHelp, setShowAdminHelp] = useState(() => {
    if (typeof window === 'undefined') return true
    try { return window.localStorage.getItem('punch.showAdminHelp') !== 'false' } catch { return true }
  })
  const [expandedAdminGroups, setExpandedAdminGroups] = useState<Set<string>>(() => new Set(['home']))
  const [panelRefreshKeys, setPanelRefreshKeys] = useState<Record<string, number>>({})
  const [postWorkNonce, setPostWorkNonce] = useState(0)
  const globalPostWorkTriggerRef = useRef<HTMLButtonElement | null>(null)

  const organizations = accessContext?.visibleOrganizations || []
  const brandingRows = accessContext?.branding?.organizations || []
  const systemSuperAdmin = Boolean(accessContext?.isSystemSuperAdmin)
  const activeOrganization = currentOrganization || (systemSuperAdmin ? null : organizations[0])
  const selectedOrgId = activeOrganization?.id || ''
  const activeBranding = brandingRows.find((branding) => brandingOrgId(branding) === activeOrganization?.id)
  const organizationUnits = accessContext?.organizationUnits || []
  const accessibleLocations = accessContext?.accessibleLocations || []
  const selectedLocation = selectedLocationId
    ? accessibleLocations.find((location) => location.id === selectedLocationId)
    : null

  useEffect(() => {
    if (selectedLocationId && !accessibleLocations.some((location) => location.id === selectedLocationId)) {
      setSelectedLocationId('')
    }
  }, [accessibleLocations, selectedLocationId])
  const selectedOu = selectedOuId
    ? organizationUnits.find((unit) => unit.id === selectedOuId)
    : null
  const platformAdmin = Boolean(accessContext?.isPlatformAdmin || accessContext?.isGeneralSystemsAdmin)
  const availableTenants = accessContext?.availableTenants || []
  const canSwitchOrganization = Boolean(
    platformAdmin
    || accessContext?.uiCapabilities?.canSwitchOrganization
    || accessContext?.canSwitchOrganizationContext
  )
  const canSwitchScope = Boolean(
    platformAdmin
    || accessContext?.uiCapabilities?.canSwitchScope
    || accessContext?.canSwitchScopeContext
  )
  const scopeLabel = selectedLocation
    ? `Location: ${selectedLocation.name || selectedLocation.label || selectedLocation.location_key || 'Selected location'}`
    : selectedOu
      ? `OU: ${selectedOu.name}`
      : activeOrganization
        ? `Organization: ${activeOrganization.name}`
        : `Tenant: ${safeTenantLabel(accessContext?.tenant, accessContext?.tenantId)}`
  const isSupervisorDefault = accessContext?.navigationHints?.preferredWorkspace === 'supervisor-dashboard'
    || accessContext?.uiCapabilities?.defaultWorkspace === 'supervisor-dashboard'
  const canAccessAdminWorkspace = Boolean(
    accessContext?.uiCapabilities?.canAccessAdminWorkspace
    || accessContext?.corporateProfile
    || organizations.length
    || accessibleLocations.length
    || accessContext?.tenantMemberships?.some((membership) => membership.membership_type === 'tenant_admin')
    || hasAnyPermission(['tenant.dashboard.view', 'tenant.locations.view', 'tenant.locations.manage', 'tenant.organizations.view', 'tenant.organizations.manage'])
  )

  const canManageOrganizations = hasAnyPermission(['tenant.organizations.manage'])
  const canManageBranding = hasAnyPermission(['tenant.branding.manage', 'tenant.organizations.manage', 'tenant.rbac.manage'])
  const canManageSettings = hasAnyPermission(['tenant.settings.manage', 'tenant.messages.manage', 'tenant.rbac.manage'])
  const canManageAdminExperience = systemSuperAdmin || hasAnyPermission(['tenant.settings.manage'])
  const canManageLocationPrivacy = hasAnyPermission(['tenant.location_privacy.manage'])
  const canViewUsers = hasAnyPermission(['tenant.users.view', 'tenant.roles.view'])
  const canManageUsers = hasAnyPermission(['tenant.users.manage', 'tenant.users.suspend', 'tenant.users.reset_password'])
  const canManageAccess = hasAnyPermission(['tenant.roles.manage', 'tenant.scoped_roles.assign', 'tenant.scoped_roles.revoke'])
  const canViewWorkers = hasAnyPermission(['tenant.workers.view'])
  const canManageWorkers = hasAnyPermission(['tenant.workers.manage'])
  const canShareWorkers = hasAnyPermission(['tenant.workers.share'])
  const canManageDocumentConsent = hasAnyPermission(['tenant.worker_documents.share', 'tenant.workers.manage'])
  const canViewJobs = hasAnyPermission(['tenant.jobs.view'])
  const canManageJobs = hasAnyPermission(['tenant.jobs.create', 'tenant.jobs.manage'])
  const canViewSchedule = hasAnyPermission(['tenant.jobs.view', 'tenant.job_assignments.view', 'tenant.job_applications.view', 'tenant.jobs.manage', 'tenant.job_assignments.manage'])
  const canManageApplications = hasAnyPermission(['tenant.job_applications.view', 'tenant.job_applications.manage', 'tenant.jobs.manage'])
  const canManageAssignments = hasAnyPermission(['tenant.job_assignments.view', 'tenant.job_assignments.manage', 'tenant.jobs.manage'])
  const canApproveAssignments = hasAnyPermission(['tenant.job_assignments.approve', 'tenant.job_assignments.manage', 'tenant.jobs.manage'])
  const canManageSkills = hasAnyPermission(['tenant.skills.manage', 'tenant.jobs.manage'])
  const canGovernSkills = hasAnyPermission(['tenant.skills.manage', 'tenant.settings.manage'])
  const canManageLocations = hasAnyPermission(['tenant.locations.manage', 'tenant.geofences.manage'])
  const canViewMessages = hasAnyPermission(['tenant.messages.view', 'tenant.messages.send', 'tenant.messages.manage', 'tenant.locations.manage'])
  const canSendMessages = hasAnyPermission(['tenant.messages.send', 'tenant.messages.manage', 'tenant.locations.manage'])
  const canExportTimesheets = hasAnyPermission(['tenant.payroll.export', 'tenant.timesheets.export', 'tenant.timesheets.view', 'tenant.punches.review', 'tenant.rbac.manage'])
  const showBrandingSetupCard = Boolean(
    activeOrganization
    && canManageBranding
    && !hasCustomizedBranding(activeBranding, activeOrganization.name)
  )
  const adminTabLabel = (tab: AdminTab, fallback: string) => {
    return adminNavigationLabel(tab, fallback, { isSimple, t, explicitIndustry: industryLabels.explicitIndustry, label: industryLabels.label })
  }
  const scopedLocationNoun = industryLabels.explicitIndustry ? industryLabels.label('location') : t('Location')
  const scopedLocationsNoun = industryLabels.explicitIndustry ? industryLabels.label('locations') : t('locations')
  const workspaceTitle = industryLabels.explicitIndustry ? `${industryLabels.label('workers')} Network OS` : t('NexusWorkforce360 Workforce Network OS')
  const activeAdminHelpTopic = industryAdminHelpTopic(activeTab, adminHelpTopics[activeTab], industryLabels)
  const navigationContext: NavigationAccessInput = {
    accessContext,
    selectedTenantId,
    currentOrganization: activeOrganization,
    hasAnyPermission,
  }
  const requestedAdminTab = adminTabFromLocation()

  useEffect(() => {
    if (!accessContext?.tenantId) return
    getOnboardingReadiness().then(setSetupReadiness).catch(() => setSetupReadiness(null))
  }, [accessContext?.tenantId, accessContext?.onboardingStatus, accessContext?.onboarding?.updatedAt])

  useEffect(() => {
    if (!accessContext?.tenantId) return undefined
    return openAdminRealtimeStream(accessContext, {
      selectedOrgId: activeOrganization?.id || null,
      selectedLocationId: selectedLocation?.id || null,
      activeWorkspace: activeTab,
      onEvent: (event) => {
        if (event.type === 'access_context.changed' || event.affectedResources?.includes('access_context')) {
          refresh().catch(() => undefined)
        }
        schedulePanelInvalidation(event, (panels) => {
          setPanelRefreshKeys((current) => {
            const next = { ...current }
            panels.forEach((panel) => {
              next[panel] = (next[panel] || 0) + 1
            })
            return next
          })
        })
      },
      // Repeated ticket authorization denials mean auth is no longer valid for this
      // tenant/scope. Re-derive the access context rather than retrying the
      // stream forever; a stale/expired login surfaces as the normal
      // access-context error state instead of a silent reconnect storm.
      onAuthFailure: () => {
        refresh().catch(() => undefined)
      },
    })
  }, [accessContext?.tenantId, activeOrganization?.id, activeTab, refresh, selectedLocation?.id])

  const panelKey = (panel: AdminPanelKey) => `${panel}:${panelRefreshKeys[panel] || 0}`

  useEffect(() => {
    if (!accessContext || activeTab !== 'overview') return
    if (isSupervisorDefault) setActiveTab('overview')
  }, [accessContext, activeTab, isSupervisorDefault])

  const visibleTabs = useMemo(
    () => adminNavigationForExperience(navigationContext, isSimple),
    [accessContext, activeOrganization, selectedTenantId, hasAnyPermission, isSimple],
  )
  const groupedVisibleTabs = useMemo(() => groupedAdminNavigationEntries(visibleTabs), [visibleTabs])
  const activeNavigationEntry = visibleTabs.find((tab) => tab.key === activeTab) || visibleTabs[0] || null
  const activeAdminGroupKey = activeNavigationEntry?.group || null
  const requestedTabUnavailable = Boolean(requestedAdminTab && !visibleTabs.some((tab) => tab.key === requestedAdminTab))
  const destinationTitle = activeNavigationEntry
    ? t(adminTabLabel(activeNavigationEntry.key, activeNavigationEntry.resolveLabel({ t, explicitIndustry: industryLabels.explicitIndustry, label: industryLabels.label })))
    : t('Admin Workspace')

  useEffect(() => {
    const syncAdminTab = () => {
      const requestedTab = adminTabFromLocation()
      if (requestedTab && visibleTabs.some((tab) => tab.key === requestedTab)) {
        setActiveTab(requestedTab)
      } else if (!new URLSearchParams(window.location.search).has('workspace')) {
        // Browser Back / a parent breadcrumb returning to /admin must also
        // restore its default destination, not retain the previous child tab.
        setActiveTab(visibleTabs.some(tab => tab.key === 'overview') ? 'overview' : visibleTabs[0]?.key || 'overview')
      }
    }
    syncAdminTab()
    window.addEventListener('popstate', syncAdminTab)
    window.addEventListener(PUNCH_NAVIGATE_EVENT, syncAdminTab)
    return () => {
      window.removeEventListener('popstate', syncAdminTab)
      window.removeEventListener(PUNCH_NAVIGATE_EVENT, syncAdminTab)
    }
  }, [visibleTabs])

  useEffect(() => {
    if (loading || visibleTabs.some((tab) => tab.key === activeTab)) return
    setActiveTab(visibleTabs[0]?.key || 'overview')
  }, [activeTab, loading, visibleTabs])

  useEffect(() => {
    if (!activeAdminGroupKey) return
    setExpandedAdminGroups((current) => {
      if (current.has(activeAdminGroupKey)) return current
      const next = new Set(current)
      next.add(activeAdminGroupKey)
      return next
    })
  }, [activeAdminGroupKey])

  const updateAdminGroupExpansion = (groupKey: string, open: boolean) => {
    setExpandedAdminGroups((current) => {
      const next = new Set(current)
      if (open || groupKey === activeAdminGroupKey) next.add(groupKey)
      else next.delete(groupKey)
      return next
    })
  }

  const selectOrg = (orgId: string) => setCurrentOrganizationId(orgId)
  // Platform bootstrap: lets a system super admin create the first tenant from
  // the tenantless platform context after a fresh database rebuild. Switching
  // the access context onto the new tenant triggers a context refresh, which
  // unlocks the normal organization-creation flow.
  const handleCreateFirstTenant = async (event: React.FormEvent) => {
    event.preventDefault()
    if (creatingTenant) return
    setCreatingTenant(true)
    setCreateTenantError('')
    try {
      const tenant = await createSystemTenant({
        name: newTenantName.trim(),
        slug: newTenantSlug.trim() || undefined,
      })
      setNewTenantName('')
      setNewTenantSlug('')
      if (tenant?.id) setCurrentTenantId(tenant.id)
      else await refresh()
    } catch (err: any) {
      setCreateTenantError(err?.response?.data?.message || t('Unable to create tenant.'))
    } finally {
      setCreatingTenant(false)
    }
  }
  const handleUpdateTenantIndustry = async (industryType: IndustryType | '') => {
    if (!accessContext?.tenantId || industrySaving) return
    setIndustrySaving(true)
    setIndustryStatus('')
    try {
      await updateSystemTenantIndustry(accessContext.tenantId, industryType || null)
      setIndustryStatus(t('Industry updated.'))
      await refresh()
    } catch (err: any) {
      setIndustryStatus(err?.response?.data?.error?.message || err?.response?.data?.message || t('Unable to update tenant industry.'))
    } finally {
      setIndustrySaving(false)
    }
  }
  const selectTab = (tabId: AdminTab) => {
    setFocusedWorkerProfileId('')
    setActiveTab(tabId)
    const nextUrl = adminTabUrl(tabId)
    if (typeof window !== 'undefined' && nextUrl !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      navigateTo(nextUrl)
    }
  }
  const canNavigateToTab = (tabId: string) => visibleTabs.some((tab) => tab.key === tabId)
  const navigateToVisibleTab = (tabId: string) => {
    if (!canNavigateToTab(tabId)) return
    selectTab(tabId as AdminTab)
  }
  const openJobDraftForLocation = (location: AccessibleLocation, source: LocationJobDraft['source']) => {
    const scopedLocation = accessibleLocations.find((item) => item.id === location.id)
    if (!scopedLocation || !canManageJobs) return
    const ownerOrgId = getAccessibleLocationOwnerOrgId(scopedLocation)
    if (ownerOrgId && organizations.some((org) => org.id === ownerOrgId)) selectOrg(ownerOrgId)
    setPendingJobDraft({
      nonce: Date.now(),
      locationId: scopedLocation.id,
      ownerOrgId,
      source,
    })
    setFocusedJobId('')
    setActiveTab('jobs')
  }
  const handleOpenRelationship = (relationshipId: string) => {
    setPreOpenRelationshipId(relationshipId)
    selectTab('relationships')
  }

  /** Navigate to Locations & Geofences tab and highlight the client company's granted locations */
  const handleViewClientLocations = (entry: RelationshipAccessEntry) => {
    setHighlightClientOrgId(entry.companyOrg?.id || entry.relationshipId)
    selectTab('locations')
  }

  /** Navigate to Demand / Jobs tab with delegated relationship context pre-seeded */
  const handleCreateDelegatedJob = (entry: RelationshipAccessEntry) => {
    setPendingDelegatedJobContext({
      nonce: Date.now(),
      relationshipId: entry.relationshipId,
      companyOrgId: entry.companyOrg?.id || '',
      companyOrgName: entry.companyOrg?.name || '',
      agencyOrgId: entry.agencyOrg?.id || '',
      agencyOrgName: entry.agencyOrg?.name || '',
      locationId: null,
      locationTenantId: null,
      locationName: null,
      grantedLocations: entry.grantedLocations || [],
    })
    selectTab('jobs')
  }

  /** Navigate to Demand / Jobs tab with a specific granted location pre-seeded */
  const handleCreateDelegatedJobFromGrantedLocation = (entry: RelationshipAccessEntry, loc: GrantedClientLocation) => {
    setPendingDelegatedJobContext({
      nonce: Date.now(),
      relationshipId: entry.relationshipId,
      companyOrgId: entry.companyOrg?.id || '',
      companyOrgName: entry.companyOrg?.name || '',
      agencyOrgId: entry.agencyOrg?.id || '',
      agencyOrgName: entry.agencyOrg?.name || '',
      locationId: loc.locationId,
      locationTenantId: loc.locationTenantId || null,
      locationName: loc.name || loc.locationKey || null,
      grantedLocations: entry.grantedLocations || [],
    })
    selectTab('jobs')
  }

  const updateShowAdminHelp = (value: boolean) => {
    setShowAdminHelp(value)
    try { window.localStorage.setItem('punch.showAdminHelp', value ? 'true' : 'false') } catch { /* Optional help preference. */ }
  }

  useEffect(() => {
    if (!initialJobDraft) return
    if (loading) return
    const scopedLocation = accessibleLocations.find((location) => location.id === initialJobDraft.locationId)
    if (!scopedLocation || !canManageJobs) {
      onConsumeInitialJobDraft?.()
      return
    }
    const ownerOrgId = initialJobDraft.ownerOrgId || getAccessibleLocationOwnerOrgId(scopedLocation)
    if (ownerOrgId && organizations.some((org) => org.id === ownerOrgId)) selectOrg(ownerOrgId)
    setPendingJobDraft({ ...initialJobDraft, ownerOrgId })
    setFocusedJobId('')
    setActiveTab('jobs')
  }, [initialJobDraft?.nonce, accessibleLocations, canManageJobs, loading, organizations])

  if (loading) {
    return (
      <section className="panel workspace-panel stack" data-testid="admin-workspace">
        <h2 className="heading-lg">{workspaceTitle}</h2>
        <p className="text-muted">{industryLabels.explicitIndustry ? `Loading your network access, organizations, and ${industryLabels.label('locations').toLowerCase()}...` : t('Loading your network access, organizations, and locations...')}</p>
      </section>
    )
  }

  if (error) {
    return (
      <section className="panel workspace-panel stack" data-testid="admin-workspace">
        <h2 className="heading-lg">{workspaceTitle}</h2>
        <p className="error-text">{error}</p>
        <p className="text-muted">{t('We could not load your workspace permissions yet. You can retry, and backend permissions still protect every action.')}</p>
        <button type="button" className="btn btn-secondary" onClick={refresh}>{t('Retry')}</button>
      </section>
    )
  }

  if (accessContext && !canAccessAdminWorkspace) {
    return (
      <section className="panel workspace-panel stack" data-testid="admin-workspace">
        <h2 className="heading-lg">{workspaceTitle}</h2>
        <p className="text-muted">{t('You are signed in, but this account does not have Admin Workspace access.')}</p>
      </section>
    )
  }

  if (accessContext && accessContext.isSystemSuperAdmin && !hasExplicitTenantContext(navigationContext)) {
    return (
      <section className="panel workspace-panel stack admin-workspace" data-testid="admin-workspace">
        <div className="admin-workspace-header admin-workspace-header--compact">
          <div className="stack" style={{ gap: '.35rem' }}>
            <h2 className="heading-lg">{t('Tenant operations need a selected tenant')}</h2>
            <p className="text-muted">
              {t('You are in platform scope. Select a tenant to open tenant-owned Admin destinations, or return to Platform Console.')}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigateTo('/platform')}
            data-testid="go-to-platform-console"
          >
            {t('Go to Platform Console')}
          </button>
        </div>
        {availableTenants.length > 0 ? (
          <div className="admin-context-panel admin-context-panel--compact">
            <label className="helper" htmlFor="tenant-required-select">{t('Tenant')}</label>
            <select
              id="tenant-required-select"
              className="input"
              value={selectedTenantId}
              onChange={(event) => setCurrentTenantId(event.target.value)}
              aria-label={t('Switch tenant context')}
              data-testid="admin-tenant-switcher"
            >
              <option value="">{t('Platform scope — select a tenant…')}</option>
              {availableTenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>{safeTenantLabel(tenant, tenant.id)}</option>
              ))}
            </select>
            <span className="helper">{t('Selecting a tenant does not make you a tenant member; backend permissions still decide each action.')}</span>
          </div>
        ) : (
          <form
            className="stack"
            style={{ gap: '0.5rem', maxWidth: '28rem' }}
            onSubmit={handleCreateFirstTenant}
            data-testid="platform-create-first-tenant"
          >
            <strong>{t('No tenant is available')}</strong>
            <span className="helper">
              {t('Create the first tenant workspace to start onboarding organizations.')}
            </span>
            <input
              className="input"
              value={newTenantName}
              onChange={(event) => setNewTenantName(event.target.value)}
              placeholder={t('Tenant name')}
              aria-label={t('Tenant name')}
              minLength={2}
              maxLength={120}
              required
            />
            <input
              className="input"
              value={newTenantSlug}
              onChange={(event) => setNewTenantSlug(event.target.value)}
              placeholder={t('Tenant slug (optional)')}
              aria-label={t('Tenant slug (optional)')}
              maxLength={80}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={creatingTenant || newTenantName.trim().length < 2}
            >
              {creatingTenant ? t('Creating tenant…') : t('Create tenant')}
            </button>
            {createTenantError ? <span className="form-error">{createTenantError}</span> : null}
          </form>
        )}
      </section>
    )
  }

  return (
    <section className="panel workspace-panel stack admin-workspace" data-testid="admin-workspace">
      {/* Compact context toolbar: the branded application header lives in
          Navigation; this strip only carries scope context and selectors. */}
      <div className="admin-context-toolbar" data-testid="admin-context-toolbar">
          <div className="admin-context-strip" aria-label={t('Access context')}>
            <div className="admin-context-primary">
              <span className="helper">{activeOrganization ? t('Organization') : t('Tenant')}</span>
              <strong>{activeOrganization?.name || safeTenantLabel(accessContext?.tenant, accessContext?.tenantId)}</strong>
              <span className="helper">
                {activeOrganization ? t(formatOrgType(activeOrganization.org_type)) : t('Tenant Admin')}
                {systemSuperAdmin ? ` · ${t('platform user')}` : ''}
              </span>
            </div>
            <div>
              <span className="helper">{t('Tenant')}</span>
              <strong>{safeTenantLabel(accessContext?.tenant, accessContext?.tenantId)}</strong>
            </div>
            <div>
              <span className="helper">{t('Scope / role')}</span>
              <strong>{currentRoleSummary(accessContext?.roles || [], t)}</strong>
              <span className="helper">{scopeLabel.replace(/^Organization:.+$/, t('All visible organization data')).replace(/^Location:/, `${scopedLocationNoun}:`)}</span>
            </div>
            {systemSuperAdmin ? <span className="status-pill status-pill--approved">{t('System Super Admin')}</span> : null}
            {platformAdmin ? <span className="status-pill status-pill--approved">{t('Platform admin')}</span> : null}
            {canManageJobs ? (
              <button
                ref={globalPostWorkTriggerRef}
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setActiveTab('jobs')
                  setPostWorkNonce(Date.now())
                }}
                data-testid="admin-post-work"
              >
                + {t('Post Work')}
              </button>
            ) : null}
            {systemSuperAdmin ? (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => navigateTo('/platform')}
                data-testid="go-to-platform-console"
              >
                {t('Platform Console')}
              </button>
            ) : null}
          </div>
          <div className="admin-context-controls">
          {systemSuperAdmin && availableTenants.length > 0 ? (
            <div className="stack" style={{ gap: '0.35rem' }}>
              <select
                className="input"
                value={selectedTenantId}
                onChange={(event) => setCurrentTenantId(event.target.value)}
                aria-label={t('Switch tenant context')}
                data-testid="admin-tenant-switcher"
              >
                <option value="">{t('Platform scope — select a tenant…')}</option>
                {availableTenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>{safeTenantLabel(tenant, tenant.id)}</option>
                ))}
              </select>
              {selectedTenantId ? (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setCurrentTenantId('')}
                  data-testid="clear-tenant-context"
                >
                  {t('Clear tenant context')}
                </button>
              ) : (
                <>
                  <span className="helper">{t('Select a tenant to manage tenant-owned data.')}</span>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigateTo('/platform')}
                    data-testid="go-to-platform-console"
                  >
                    {t('Go to Platform Console')}
                  </button>
                </>
              )}
            </div>
          ) : null}
          {systemSuperAdmin && accessContext?.tenantId ? (
            <div className="stack" style={{ gap: '0.35rem' }} data-testid="tenant-industry-control">
              <label className="helper" htmlFor="tenant-industry-select">{t('Industry Type')}</label>
              <select
                id="tenant-industry-select"
                className="input"
                value={(accessContext.industryType || accessContext.tenant?.industry_type || '') as string}
                onChange={(event) => handleUpdateTenantIndustry(event.target.value as IndustryType | '')}
                disabled={industrySaving}
              >
                <option value="">{t('General Workforce Operations')}</option>
                {availableIndustryTypes
                  .filter((industry) => industry.key !== 'general_workforce')
                  .map((industry) => (
                    <option key={industry.key} value={industry.key}>{t(industry.displayName)}</option>
                  ))}
              </select>
              <span className="helper">
                {t(getIndustryPreset(accessContext.industryType || accessContext.tenant?.industry_type).shortDescription)}
              </span>
              {industryStatus ? <span className={industryStatus.includes('Unable') ? 'form-error' : 'helper'}>{industryStatus}</span> : null}
            </div>
          ) : null}
          {systemSuperAdmin && availableTenants.length === 0 ? (
            <form
              className="stack"
              style={{ gap: '0.5rem', maxWidth: '28rem' }}
              onSubmit={handleCreateFirstTenant}
              data-testid="platform-create-first-tenant"
            >
              <strong>{t('Create the first tenant')}</strong>
              <span className="helper">
                {t('No tenants exist yet. Create the first tenant workspace to start onboarding organizations.')}
              </span>
              <input
                className="input"
                value={newTenantName}
                onChange={(event) => setNewTenantName(event.target.value)}
                placeholder={t('Tenant name')}
                aria-label={t('Tenant name')}
                minLength={2}
                maxLength={120}
                required
              />
              <input
                className="input"
                value={newTenantSlug}
                onChange={(event) => setNewTenantSlug(event.target.value)}
                placeholder={t('Tenant slug (optional)')}
                aria-label={t('Tenant slug (optional)')}
                maxLength={80}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={creatingTenant || newTenantName.trim().length < 2}
              >
                {creatingTenant ? t('Creating tenant…') : t('Create tenant')}
              </button>
              {createTenantError ? <span className="form-error">{createTenantError}</span> : null}
            </form>
          ) : null}
          {organizations.length > 1 && canSwitchOrganization ? (
            <select
              className="input"
              value={selectedOrgId}
              onChange={(event) => selectOrg(event.target.value)}
              aria-label={t('Switch organization context')}
              data-testid="admin-org-switcher"
            >
              {systemSuperAdmin ? <option value="">{t('No organization selected')}</option> : null}
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>{org.name} - {t(formatOrgType(org.org_type))}</option>
              ))}
            </select>
          ) : (
            <span className="helper">
              {organizations.length > 1
                ? t('Organization switching is read-only for your current access.')
                : activeOrganization ? t('Organization context is fixed for your current access.') : t('No selectable organization is available.')}
            </span>
          )}
          {organizationUnits.length > 1 && canSwitchScope ? (
            <select
              className="input"
              value={selectedOuId}
              onChange={(event) => setSelectedOuId(event.target.value)}
              aria-label={t('Switch organization unit context')}
            >
              <option value="">{t('All visible OUs')}</option>
              {organizationUnits
                .filter((unit) => !selectedOrgId || unit.org_id === selectedOrgId)
                .map((unit) => <option key={unit.id} value={unit.id}>{unit.name} - {readableScopeType(unit.ou_type)}</option>)}
            </select>
          ) : null}
          {accessibleLocations.length > 1 && canSwitchScope ? (
            <select
              className="input"
              value={selectedLocationId}
              onChange={(event) => setSelectedLocationId(event.target.value)}
              aria-label={t('Switch location context')}
            >
              <option value="">{industryLabels.explicitIndustry ? `All visible ${scopedLocationsNoun}` : t('All visible locations')}</option>
              {accessibleLocations
                .filter((location) => !selectedOrgId || (location.ownerOrgId || location.owner_org_id) === selectedOrgId)
                .map((location) => <option key={location.id} value={location.id}>{location.name || location.label || location.location_key}</option>)}
            </select>
          ) : null}
          {selectedLocation ? (
            <span className="helper">
              {scopedLocationNoun}: {selectedLocation.name || selectedLocation.label || selectedLocation.location_key || (industryLabels.explicitIndustry ? `Selected ${industryLabels.label('location').toLowerCase()}` : t('Selected location'))}
            </span>
          ) : accessibleLocations.length === 1 ? (
            <span className="helper">
              {scopedLocationNoun}: {accessibleLocations[0].name || accessibleLocations[0].label || accessibleLocations[0].location_key || (industryLabels.explicitIndustry ? `Available ${industryLabels.label('location').toLowerCase()}` : t('Available location'))}
            </span>
          ) : null}
          {!canSwitchScope ? <span className="helper">{t('Scope selectors are read-only unless your role can switch tenant, organization, OU, or location context.')}</span> : null}
          <div className="admin-color-hints" aria-label={t('Organization brand colors')}>
            {brandColors(activeBranding).map((color) => (
              <span key={color} className="admin-color-swatch" style={{ background: color }} title={color} />
            ))}
          </div>
          <label className="admin-help-toggle">
            <input
              type="checkbox"
              checked={showAdminHelp}
              onChange={(event) => updateShowAdminHelp(event.target.checked)}
            />
            <span>{t('Help')}</span>
          </label>
          </div>
      </div>

      <div className="admin-workspace-layout">
      <nav className="admin-sidebar" aria-label={t('Admin workspace sections')} data-testid="admin-sidebar">
      <div className="admin-navigation-groups">
        {groupedVisibleTabs.map((group) => {
          const groupIsActive = group.key === activeAdminGroupKey
          const groupIsOpen = groupIsActive || expandedAdminGroups.has(group.key)
          return (
            <details
              key={group.key}
              className={`admin-navigation-group ${groupIsActive ? 'admin-navigation-group--active' : ''}`}
              open={groupIsOpen}
              onToggle={(event) => updateAdminGroupExpansion(group.key, event.currentTarget.open)}
            >
              <summary
                aria-expanded={groupIsOpen}
                onKeyDown={(event) => {
                  if (!groupIsActive || (event.key !== 'Enter' && event.key !== ' ')) return
                  event.preventDefault()
                  updateAdminGroupExpansion(group.key, true)
                }}
              >
                <span className="admin-navigation-group__label">{t(group.label)}</span>
                <span className="admin-navigation-group__count" aria-label={`${group.entries.length} ${t('destinations')}`}>
                  {group.entries.length}
                </span>
              </summary>
              <div className="admin-tabs" role="group" aria-label={t(group.label)}>
                {group.entries.map((tab: AdminNavigationEntry) => {
                  const billingLocked = Boolean(tab.billingFeatureKey && accessContext?.billingEntitlements?.features?.[tab.billingFeatureKey]?.enabled !== true)
                  return (
                  <a
                    key={tab.key}
                    href={adminTabUrl(tab.key)}
                    className={`admin-tab ${activeTab === tab.key ? 'active' : ''} ${billingLocked ? 'admin-tab--billing-locked' : ''}`}
                    onClick={event => {
                      if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
                      event.preventDefault()
                      selectTab(tab.key)
                    }}
                    aria-current={activeTab === tab.key ? 'page' : undefined}
                    data-testid={`admin-tab-${tab.key}`}
                    title={t(adminTabLabel(tab.key, tab.resolveLabel({ t, explicitIndustry: industryLabels.explicitIndustry, label: industryLabels.label })))}
                  >
                    {t(adminTabLabel(tab.key, tab.resolveLabel({ t, explicitIndustry: industryLabels.explicitIndustry, label: industryLabels.label })))}
                    {billingLocked ? <span className="admin-tab__billing-lock">🔒 {t('Contact Sales')}</span> : null}
                  </a>
                  )
                })}
              </div>
            </details>
          )
        })}
      </div>
      </nav>

      <div className="admin-main-content stack">
      {organizations.length === 0 ? (
        <div className="panel workspace-panel admin-empty-state stack" data-testid="admin-onboarding-prompt">
          <div className="stack" style={{ gap: '.35rem' }}>
            <h3 style={{ margin: 0 }}>{t('Create an organization to finish setup')}</h3>
            <p className="text-muted">
              {t('Your account is signed in, but there is no organization in the current tenant context yet.')}
            </p>
          </div>
          <button type="button" className="btn btn-primary" onClick={() => setActiveTab('organizations')}>
            {t('Create tenant organization')}
          </button>
        </div>
      ) : null}

      <SetupReminderCard readiness={setupReadiness} />
      {!setupReadiness ? (
        <SetupChecklist
          setupState={accessContext?.setupState}
          canTroubleshootPunches={Boolean(accessContext?.mobileCapabilities?.canTroubleshootPunches)}
          labels={industryLabels.label}
          explicitIndustry={industryLabels.explicitIndustry}
        />
      ) : null}

      {showBrandingSetupCard ? (
        <article className="panel workspace-panel admin-setup-card stack" data-testid="branding-setup-card">
          <div className="admin-panel-toolbar">
            <div className="stack" style={{ gap: '.25rem' }}>
              <h3 style={{ margin: 0 }}>{t('Customize your company/agency branding')}</h3>
              <p className="text-muted">
                {t('Add a display name, logo URL, brand colors, and support contact details for')} {activeOrganization?.name}.
              </p>
            </div>
            <button type="button" className="btn btn-primary" onClick={() => setActiveTab('settings')}>
              {t('Open branding')}
            </button>
          </div>
        </article>
      ) : null}

      <AdminHelp
        topic={activeAdminHelpTopic}
        showHelp={showAdminHelp}
        onDismiss={() => updateShowAdminHelp(false)}
      />

      {requestedTabUnavailable ? (
        <section className="admin-destination-panel stack" data-testid="admin-destination-permission-limited">
          <header className="admin-page-header" aria-label={t('Admin destination heading')}>
            <div className="admin-page-header__copy">
              <div className="admin-page-header__context">{t('Admin destination')}</div>
              <div className="admin-page-header__title-row">
                <h1 id="admin-destination-unavailable-title">{t('Admin destination unavailable')}</h1>
              </div>
              <p>{t('The requested Admin destination is not available in your current tenant, organization, or location scope.')}</p>
            </div>
          </header>
          <div className="admin-state admin-state--permission-limited">
            <strong>{t('Permission-limited route')}</strong>
            <p>{t('No settings, branding, organization, billing, or tenant-wide configuration controls are rendered for this route.')}</p>
          </div>
        </section>
      ) : (
        <section className="admin-destination-panel stack" data-testid="admin-destination-panel">
          {['locations', 'organizations', 'workers', 'settings', 'jobs'].includes(activeTab) ? (
            <AdminPageHeader
              title={destinationTitle}
              titleId="admin-destination-title"
              breadcrumbLabel={t('Page hierarchy')}
              breadcrumbs={[
                { label: t('Admin Workspace'), href: adminTabUrl('overview') },
                { label: destinationTitle },
              ]}
            />
          ) : (
          <header className="admin-page-header" aria-label={t('Admin destination heading')}>
            <div className="admin-page-header__copy">
              <div className="admin-page-header__context">{t('Admin destination')}</div>
              <div className="admin-page-header__title-row">
                <h1 id="admin-destination-title">{destinationTitle}</h1>
              </div>
            </div>
          </header>
          )}

          <AdminProcessGuide tab={activeTab} accessContext={accessContext} />

          {activeTab === 'schedule' && <SupervisorDashboardPanel key={`${panelKey('schedule')}:operations`} accessContext={accessContext} accessibleLocations={accessibleLocations} />}
      {activeTab === 'overview' ? (
        isSupervisorDefault ? (
          <SupervisorDashboardPanel key={panelKey('overview')} accessContext={accessContext} accessibleLocations={accessibleLocations} />
        ) : (
          <AdminOverviewPanel
            key={panelKey('overview')}
            accessContext={accessContext}
            organizations={organizations}
            selectedOrgId={selectedOrgId}
            canNavigateTo={canNavigateToTab}
            onNavigate={navigateToVisibleTab}
            onOpenRelationship={handleOpenRelationship}
            onViewClientLocations={handleViewClientLocations}
            onCreateDelegatedJob={handleCreateDelegatedJob}
          />
        )
      ) : null}

      {activeTab === 'organizations' ? (
        <OrganizationsPanel
          key={panelKey('organizations')}
          organizations={organizations}
          brandingRows={brandingRows}
          organizationUnits={organizationUnits}
          organizationRelationships={accessContext?.organizationRelationships || []}
          tenant={accessContext?.tenant}
          tenantId={accessContext?.tenantId}
          canManageOrganizations={canManageOrganizations}
          onRefresh={refresh}
          onEditBranding={(orgId) => {
            selectOrg(orgId)
            setActiveTab('settings')
          }}
        />
      ) : null}

      {activeTab === 'relationships' ? (
        <OrgRelationshipsPanel
          key={panelKey('relationships')}
          organizations={organizations}
          accessibleLocations={accessibleLocations}
          selectedOrgId={selectedOrgId}
          canManageOrganizations={canManageOrganizations}
          canUsePlatformLinker={Boolean(accessContext?.isSystemAdmin || accessContext?.isSystemSuperAdmin || accessContext?.isPlatformAdmin)}
          onSelectOrg={selectOrg}
          relationshipAccess={accessContext?.relationshipAccess || []}
          initialRelationshipId={preOpenRelationshipId}
        />
      ) : null}

      {activeTab === 'org-tree' ? (
        <OrgTreePanel
          key={panelKey('org-tree')}
          organizations={organizations}
          contextUnits={organizationUnits}
          accessibleLocations={accessibleLocations}
          selectedOrgId={selectedOrgId}
          canManageOrganizations={canManageOrganizations}
          onSelectOrg={selectOrg}
        />
      ) : null}

      {activeTab === 'users-access' ? (
        <UsersAccessPanel
          key={panelKey('users-access')}
          organizations={organizations}
          organizationUnits={organizationUnits}
          accessibleLocations={accessibleLocations}
          selectedOrgId={selectedOrgId}
          canViewUsers={canViewUsers}
          canManageUsers={canManageUsers}
          canManageRoles={canManageAccess}
          onSelectOrg={selectOrg}
          onRefresh={refresh}
        />
      ) : null}

      {activeTab === 'employment' && <EmploymentCompliancePanel />}
      {activeTab === 'workers' ? (
        <WorkersPanel
          key={panelKey('workers')}
          organizations={organizations}
          selectedOrgId={selectedOrgId}
          canViewWorkers={canViewWorkers}
          canManageWorkers={canManageWorkers}
          canShareWorkers={canShareWorkers}
          canManageDocumentConsent={canManageDocumentConsent}
          requiresExplicitOrgContext={systemSuperAdmin || platformAdmin}
          initialSelectedWorkerProfileId={focusedWorkerProfileId}
          onClearInitialSelectedWorkerProfileId={() => setFocusedWorkerProfileId('')}
          onSelectOrg={selectOrg}
        />
      ) : null}

      {activeTab === 'settings' ? (
        <>
          {selectedOrgId&&canManageOrganizations?<ActivationGuidance key={`activation:${selectedOrgId}`} orgId={selectedOrgId}/>:null}
          {canManageAdminExperience?<ActivationPolicyEditor/>:null}
          {selectedOrgId && canManageOrganizations ? <IndustryWorkspaceTemplates key={selectedOrgId} orgId={selectedOrgId} /> : null}
          <AdminExperienceSettingsPanel canManage={canManageAdminExperience} />
          {accessContext?.tenantId ? <WorkerOnboardingPolicyPanel key={`${accessContext.currentUser?.id}:${accessContext.tenantId}`} tenantId={accessContext.tenantId} canManage={canManageAdminExperience} /> : null}
          <article className="panel workspace-panel admin-setup-card stack" data-testid="setup-wizard-card">
            <div className="admin-panel-toolbar">
              <div className="stack" style={{ gap: '.25rem' }}>
                <h3 style={{ margin: 0 }}>{t('Setup Wizard')}</h3>
                <p className="text-muted">
                  {industryLabels.explicitIndustry ? `Review or edit company profile, ${industryLabels.label('locations').toLowerCase()}, geofences, supervisors, ${industryLabels.label('workers').toLowerCase()}, and launch readiness.` : t('Review or edit company profile, locations, geofences, supervisors, workers, and launch readiness.')}
                </p>
              </div>
              <button type="button" className="btn btn-primary" onClick={() => {
                navigateTo('/setup')
              }}>
                {t('Open setup')}
              </button>
            </div>
          </article>
          <BillingFeatureGate featureKey="branding.white_label" title={t('White-label Branding')} description={t('Customize organization identity, colors, logos, banners, and support details.')}>
            <BrandingPanel
              key={panelKey('settings')}
              organizations={organizations}
              brandingRows={brandingRows}
              selectedOrgId={selectedOrgId}
              canManageBranding={canManageBranding}
              onSelectOrg={selectOrg}
              onRefresh={refresh}
            />
          </BillingFeatureGate>
          <BillingFeatureGate
            featureKey="integrations.whatsapp"
            title={t('WhatsApp Integration')}
            description={t('Send operational reminders and worker notifications through WhatsApp.')}
          >
            <WhatsappSettingsPanel canManageSettings={canManageSettings} />
          </BillingFeatureGate>
          <AutoPunchSettingsPanel canManageSettings={canManageSettings} />
          <BillingFeatureGate featureKey="compliance.labour_rules" title={t('Labour Rule Profiles')} description={t('Configure and inspect effective jurisdiction profiles and automatic advisories.')}>
            <LabourStandardsPanel />
          </BillingFeatureGate>
          <BillingFeatureGate featureKey="compliance.location_privacy" title={t('Location Privacy')} description={t('Control purpose-bound collection, retention, and evidence holds.')}>
            <LocationPrivacyAdminPanel canManage={canManageLocationPrivacy} />
          </BillingFeatureGate>
          <BillingFeatureGate featureKey="integrations.openapi" title={t('OpenAPI Contract')} description={t('Inspect the canonical versioned integration contract.')}>
            <OpenApiStandardsPanel />
          </BillingFeatureGate>
          <BillingFeatureGate featureKey="integrations.webhooks" title={t('Webhook Integrations')} description={t('Inspect signed webhook subscriptions and delivery state.')}>
            <WebhookStandardsPanel />
          </BillingFeatureGate>
        </>
      ) : null}

      {activeTab === 'locations' ? (
        <LocationsPanel
          key={panelKey('locations')}
          organizations={organizations}
          brandingRows={brandingRows}
          organizationUnits={organizationUnits}
          accessibleLocations={accessibleLocations}
          canManageLocations={canManageLocations}
          canCreateJobs={canManageJobs}
          canUseLiveTracking={accessContext?.billingEntitlements?.features?.['workforce.live_tracking']?.enabled === true}
          relationshipAccess={accessContext?.relationshipAccess || []}
          currentOrgId={accessContext?.currentOrganization?.id || organizations[0]?.id || ''}
          highlightClientOrgId={highlightClientOrgId}
          onCreateJobFromLocation={(location) => openJobDraftForLocation(location, 'locations')}
          onCreateDelegatedJobFromGrantedLocation={handleCreateDelegatedJobFromGrantedLocation}
          onRefresh={refresh}
          suppressPageHeader
        />
      ) : null}

      {activeTab === 'jobs' ? (
        <JobsPanel
          key={panelKey('jobs')}
          organizations={organizations}
          brandingRows={brandingRows}
          accessibleLocations={accessibleLocations}
          organizationRelationships={accessContext?.organizationRelationships || []}
          workerRelationships={accessContext?.workerOrgRelationships || []}
          relationshipAccess={accessContext?.relationshipAccess || []}
          selectedOrgId={selectedOrgId}
          canViewJobs={canViewJobs}
          canManageJobs={canManageJobs}
          canManageApplications={canManageApplications}
          canManageAssignments={canManageAssignments}
          canApproveAssignments={canApproveAssignments}
          canManageSkills={canManageSkills}
          canViewPayRate={accessContext?.rateVisibility?.canViewPayRate === true}
          canViewBillRate={accessContext?.rateVisibility?.canViewBillRate === true}
          lifecyclePermissions={accessContext?.lifecyclePermissions}
          initialSelectedJobId={focusedJobId}
          initialJobDraft={pendingJobDraft}
          delegatedJobContext={pendingDelegatedJobContext}
          onConsumeInitialJobDraft={() => {
            setPendingJobDraft(null)
            onConsumeInitialJobDraft?.()
          }}
          onConsumeDelegatedJobContext={() => setPendingDelegatedJobContext(null)}
          onSelectOrg={selectOrg}
          onOpenSchedule={() => setActiveTab('schedule')}
          openPostWorkNonce={postWorkNonce}
          onPostWorkClosed={() => globalPostWorkTriggerRef.current?.focus()}
        />
      ) : null}

      {activeTab === 'skills' ? (
        <SkillsGovernancePanel key={panelKey('skills')} canManageSkills={canGovernSkills} />
      ) : null}

      {activeTab === 'worklog' ? (
        <BillingFeatureGate featureKey="worklog.project_time" title={t('Project Work Log')} description={t('Manage projects, tasks, daily project time, and weekly project timesheets.')}>
          <ProjectWorkLogAdminPanel key={panelKey('worklog')} />
        </BillingFeatureGate>
      ) : null}

      {activeTab === 'forecasting' ? (
        <BillingFeatureGate featureKey="workforce.predictive_forecasting" title={t('Predictive Workforce Forecasting')} description={t('Forecast total site demand and translate it into stable FTE and temporary staffing plans.')}>
          <PredictiveForecastingPanel />
        </BillingFeatureGate>
      ) : null}

      {activeTab === 'schedule' ? (
        <SchedulePanel
          key={panelKey('schedule')}
          organizations={organizations}
          accessibleLocations={accessibleLocations}
          workerRelationships={accessContext?.workerOrgRelationships || []}
          selectedOrgId={selectedOrgId}
          canViewSchedule={canViewSchedule}
          canManageJobs={canManageJobs}
          canManageApplications={canManageApplications}
          canManageAssignments={canManageAssignments}
          canViewPayRate={accessContext?.rateVisibility?.canViewPayRate === true}
          canViewBillRate={accessContext?.rateVisibility?.canViewBillRate === true}
          onOpenJob={(jobId) => {
            setFocusedJobId(jobId)
            setActiveTab('jobs')
          }}
          onOpenWorkerProfile={(workerProfileId) => {
            setFocusedWorkerProfileId(workerProfileId)
            setActiveTab('workers')
          }}
        />
      ) : null}

      {activeTab === 'timesheets' ? (
        <TimesheetPayrollExportPanel
          key={`${activeTab}:${panelRefreshKeys[activeTab] || 0}`}
          mode="approvals"
          organizations={organizations}
          accessibleLocations={accessibleLocations}
          canExportTimesheets={canExportTimesheets}
        />
      ) : null}

      {activeTab === 'decision-reviews' ? (
        <BillingFeatureGate featureKey="decisions.appeals" title={t('Decision Reviews')} description={t('Review worker appeals for material access decisions within your authorized scope.')}>
          <DecisionAppealsPanel accessibleLocations={accessibleLocations} />
        </BillingFeatureGate>
      ) : null}

      {activeTab === 'payroll' ? (
        <BillingFeatureGate
          featureKey="workforce.payroll_exports"
          title={t('Payroll Export')}
          description={t('Export approved timesheets to payroll-ready files.')}
        >
          <TimesheetPayrollExportPanel
            key={`${activeTab}:${panelRefreshKeys[activeTab] || 0}`}
            mode="settlement"
            organizations={organizations}
            accessibleLocations={accessibleLocations}
            canExportTimesheets={canExportTimesheets}
          />
        </BillingFeatureGate>
      ) : null}

      {activeTab === 'messages' ? (
        <BillingFeatureGate featureKey="workforce.messaging" title={t('Messaging')} description={t('Communicate with workers and operational teams in the current scope.')}>
          <MessagesPanel
            refreshVersion={panelRefreshKeys.messages || 0}
            accessibleLocations={accessibleLocations}
            workerRelationships={accessContext?.workerOrgRelationships || []}
            selectedOrgId={selectedOrgId}
            selectedLocationId={selectedLocationId}
            canViewMessages={canViewMessages}
            canSendMessages={canSendMessages}
          />
        </BillingFeatureGate>
      ) : null}

      {activeTab === 'reports' ? (
        <BillingFeatureGate featureKey="workforce.reporting" title={t('Reports')} description={t('Review scoped workforce, attendance, and approval readiness reporting.')}>
          <ReportsPanel
            key={panelKey('reports')}
            organizations={organizations}
            accessibleLocations={accessibleLocations}
            selectedOrgId={selectedOrgId}
            canViewReports={canExportTimesheets}
            onOpenPayrollExport={() => setActiveTab('payroll')}
          />
        </BillingFeatureGate>
      ) : null}
        </section>
      )}
      </div>
      </div>
    </section>
  )
}
